import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  VideoCamera, Plus, Trash, PencilSimple, X, Warning, ArrowClockwise
} from '@phosphor-icons/react';

const ROOMS = ['Living Room', 'Bedroom', 'Kitchen', 'Entrance', 'Garage', 'Garden', 'Balcony', 'Other'];

export default function CamerasPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', room: 'Living Room', stream_url: '', snapshot_url: '', brand: '', notes: '' });
  const [snapshots, setSnapshots] = useState({});

  const fetchCameras = async () => {
    try { const res = await api.get('/cameras'); setCameras(res.data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchCameras(); }, [api]);

  const refreshSnapshot = async (camId) => {
    try {
      const res = await api.get(`/cameras/${camId}/snapshot`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setSnapshots(prev => ({ ...prev, [camId]: url }));
    } catch (e) {
      setSnapshots(prev => ({ ...prev, [camId]: 'error' }));
    }
  };

  useEffect(() => {
    cameras.forEach(c => { if (c.snapshot_url) refreshSnapshot(c.camera_id); });
    const interval = setInterval(() => {
      cameras.forEach(c => { if (c.snapshot_url) refreshSnapshot(c.camera_id); });
    }, 10000);
    return () => clearInterval(interval);
  }, [cameras]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/cameras/${editing}`, form); }
      else { await api.post('/cameras', form); }
      closeForm(); fetchCameras();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/cameras/${id}`); fetchCameras(); } catch (e) { console.error(e); }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, room: c.room, stream_url: c.stream_url || '', snapshot_url: c.snapshot_url || '', brand: c.brand || '', notes: c.notes || '' });
    setEditing(c.camera_id); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm({ name: '', room: 'Living Room', stream_url: '', snapshot_url: '', brand: '', notes: '' }); };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="cameras-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Security</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('camera_feeds')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Monitor your home cameras</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-camera-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('add_camera')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {cameras.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <VideoCamera size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No cameras configured</p>
            <p className="text-xs text-neutral-600 mt-1">Add your IP camera's snapshot URL in Settings to see live feeds</p>
          </div>
        ) : cameras.map(cam => (
          <div key={cam.camera_id} className="glass-card overflow-hidden animate-fade-up" data-testid={`camera-card-${cam.camera_id}`}>
            <div className="aspect-video bg-black/40 relative">
              {snapshots[cam.camera_id] && snapshots[cam.camera_id] !== 'error' ? (
                <img src={snapshots[cam.camera_id]} alt={cam.name} className="w-full h-full object-cover" />
              ) : cam.stream_url ? (
                <img src={cam.stream_url} alt={cam.name} className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <VideoCamera size={36} className="text-neutral-600" weight="duotone" />
                </div>
              )}
              {snapshots[cam.camera_id] === 'error' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center">
                    <Warning size={24} className="mx-auto text-amber-400 mb-1" />
                    <p className="text-xs text-neutral-400">Camera offline</p>
                  </div>
                </div>
              )}
              <div className="absolute top-2 left-2 flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${cam.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                <span className="text-[10px] font-bold text-white bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm">{cam.room}</span>
              </div>
              <button onClick={() => refreshSnapshot(cam.camera_id)}
                className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white/70 hover:text-white transition-colors">
                <ArrowClockwise size={14} />
              </button>
            </div>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">{cam.name}</p>
                <p className="text-[11px] text-neutral-500">{cam.brand || 'IP Camera'}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(cam)} data-testid={`edit-camera-${cam.camera_id}`} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-white"><PencilSimple size={14} /></button>
                <button onClick={() => handleDelete(cam.camera_id)} data-testid={`delete-camera-${cam.camera_id}`} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{editing ? 'Edit Camera' : t('add_camera')}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">{t('name')}</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="camera-name-input" placeholder="Front Door" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">Room</label><select value={form.room} onChange={e => setForm({...form, room: e.target.value})} data-testid="camera-room-select" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">{ROOMS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
              </div>
              <div><label className="overline block mb-2">{t('snapshot_url')}</label><input type="url" value={form.snapshot_url} onChange={e => setForm({...form, snapshot_url: e.target.value})} data-testid="camera-snapshot-input" placeholder="http://192.168.1.x/snapshot.jpg" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /><p className="text-[10px] text-neutral-500 mt-1">JPG snapshot URL from your IP camera</p></div>
              <div><label className="overline block mb-2">{t('stream_url')}</label><input type="url" value={form.stream_url} onChange={e => setForm({...form, stream_url: e.target.value})} data-testid="camera-stream-input" placeholder="http://192.168.1.x/mjpeg" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /><p className="text-[10px] text-neutral-500 mt-1">MJPEG stream URL (optional, for live view)</p></div>
              <div><label className="overline block mb-2">{t('brand')}</label><input type="text" value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} data-testid="camera-brand-input" placeholder="e.g., Hikvision, TP-Link" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-camera-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
